import { DialogComponent } from '@theme/dialog';
import { CartAddEvent, ThemeEvents, CartUpdateEvent } from '@theme/events';

// Type definitions for Auto Gift Manager
/**
 * @typedef {Object} GiftRule
 * @property {string} id
 * @property {string} name
 * @property {number} minimumValue
 * @property {string} giftProductId
 * @property {number} quantity
 * @property {boolean} enabled
 * @property {string} property
 * @property {string} propertyValue
 */

/**
 * @typedef {Object} CartItem
 * @property {number} product_id
 * @property {number} variant_id
 * @property {number} quantity
 * @property {Object} properties
 * @property {string} key
 */

/**
 * @typedef {Object} CartState
 * @property {number} total_price
 * @property {number} item_count
 * @property {CartItem[]} items
 */

/**
 * @typedef {Object} CartDrawerSettings
 * @property {number} goalProduct1Threshold
 * @property {number} goalProduct2Threshold
 * @property {number} goalProduct3Threshold
 * @property {string} freeProductVariantId1
 * @property {string} freeProductVariantId2
 * @property {string} freeProductVariantId3
 * @property {number} productHasToBeInCart
 */

// Type assertion for window.cartDrawerSettings
/** @type {() => CartDrawerSettings} */
const getCartDrawerSettings = () => {
  if (typeof window !== 'undefined' && /** @type {any} */ (window).cartDrawerSettings) {
    return /** @type {CartDrawerSettings} */ (/** @type {any} */ (window).cartDrawerSettings);
  }
  return /** @type {CartDrawerSettings} */ ({});
};

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    
    // Initialize Auto Gift Manager
    this.initializeAutoGiftManager();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  /**
   * Handles cart update events to refresh progress bars when cart changes
   * @param {CartUpdateEvent} event - The cart update event
   */
  #handleCartUpdate = (event) => {
    // Only update progress bars if the cart drawer is currently open
    if (this.refs.dialog && this.refs.dialog.open) {
      // Wait for the morphing to complete before updating progress bars
      // Use a longer delay to ensure the DOM has been fully morphed
      
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });

    // Animate progress bars when drawer opens
    this.#animateCartProgressBars();
  }

  close() {
    this.closeDialog();
  }

  // Animate progress bars on cart drawer open
  #animateCartProgressBars() {
    console.log('💥[animateCartProgressBars] Animating cart progress bars...');
    setTimeout(function() {
      const bar1 = /** @type {HTMLElement} */ (document.querySelector('.js-cart-progress-bar-1'));
      const bar2 = /** @type {HTMLElement} */ (document.querySelector('.js-cart-progress-bar-2'));
      const bar3 = /** @type {HTMLElement} */ (document.querySelector('.js-cart-progress-bar-3'));
      
      if (bar1) {
        const target1 = bar1.getAttribute('data-progress') || '0';
        const parent1 = bar1.closest('.cart-drawer__goal-progress');
        bar1.style.transition = 'none';
        bar1.style.width = '0%';
        void bar1.offsetWidth;
        bar1.style.transition = '';
        bar1.style.width = target1 + '%';
        if (parent1) parent1.classList.remove('filled');
        const onBar1End = function() {
          if (parseFloat(target1) === 100 && parent1) parent1.classList.add('filled');
          bar1.removeEventListener('transitionend', onBar1End);
        };
        bar1.addEventListener('transitionend', onBar1End);
        
        if (bar2) {
          const target2 = bar2.getAttribute('data-progress') || '0';
          const parent2 = bar2.closest('.cart-drawer__goal-progress');
          bar2.style.transition = 'none';
          bar2.style.width = '0%';
          void bar2.offsetWidth;
          bar2.style.transition = '';
          bar2.style.width = '0%';
          if (parent2) parent2.classList.remove('filled');
          const animateBar2 = function() {
            bar2.style.width = target2 + '%';
            const onBar2End = function() {
              if (parseFloat(target2) === 100 && parent2) parent2.classList.add('filled');
              bar2.removeEventListener('transitionend', onBar2End);
            };
            bar2.addEventListener('transitionend', onBar2End);
            bar1.removeEventListener('transitionend', animateBar2);
          };
          bar1.addEventListener('transitionend', animateBar2);
          setTimeout(function() {
            const computedWidth = parseFloat(getComputedStyle(bar1).width);
            const parentElement = /** @type {HTMLElement} */ (bar1.parentNode);
            const targetWidth = parentElement.offsetWidth * (parseFloat(target1) / 100);
            if (Math.abs(computedWidth - targetWidth) < 2 && bar2.style.width === '0%') {
              animateBar2();
            }
          }, 200);
        }
        
        if (bar3) {
          const target3 = bar3.getAttribute('data-progress') || '0';
          const parent3 = bar3.closest('.cart-drawer__goal-progress');
          bar3.style.transition = 'none';
          bar3.style.width = '0%';
          void bar3.offsetWidth;
          bar3.style.transition = '';
          bar3.style.width = '0%';
          if (parent3) parent3.classList.remove('filled');
          const animateBar3 = function() {
            bar3.style.width = target3 + '%';
            const onBar3End = function() {
              if (parseFloat(target3) === 100 && parent3) parent3.classList.add('filled');
              bar3.removeEventListener('transitionend', onBar3End);
            };
            bar3.addEventListener('transitionend', onBar3End);
          };
          
          // Chain bar3 animation after bar2 completes
          if (bar2) {
            const target2 = bar2.getAttribute('data-progress') || '0';
            const chainBar3 = function() {
              setTimeout(function() {
                const computedWidth = parseFloat(getComputedStyle(bar2).width);
                const parentElement = /** @type {HTMLElement} */ (bar2.parentNode);
                const targetWidth = parentElement.offsetWidth * (parseFloat(target2) / 100);
                if (Math.abs(computedWidth - targetWidth) < 2 && bar3.style.width === '0%') {
                  animateBar3();
                }
              }, 200);
            };
            bar2.addEventListener('transitionend', chainBar3);
          } else {
            // If no bar2, animate bar3 after bar1
            setTimeout(function() {
              const computedWidth = parseFloat(getComputedStyle(bar1).width);
              const parentElement = /** @type {HTMLElement} */ (bar1.parentNode);
              const targetWidth = parentElement.offsetWidth * (parseFloat(target1) / 100);
              if (Math.abs(computedWidth - targetWidth) < 2 && bar3.style.width === '0%') {
                animateBar3();
              }
            }, 400);
          }
        }
      } else if (bar2) {
        const target2 = bar2.getAttribute('data-progress') || '0';
        const parent2 = bar2.closest('.cart-drawer__goal-progress');
        bar2.style.transition = 'none';
        bar2.style.width = '0%';
        void bar2.offsetWidth;
        bar2.style.transition = '';
        bar2.style.width = target2 + '%';
        if (parent2) parent2.classList.remove('filled');
        const onBar2End = function() {
          if (parseFloat(target2) === 100 && parent2) parent2.classList.add('filled');
          bar2.removeEventListener('transitionend', onBar2End);
        };
        bar2.addEventListener('transitionend', onBar2End);
        
        if (bar3) {
          const target3 = bar3.getAttribute('data-progress') || '0';
          const parent3 = bar3.closest('.cart-drawer__goal-progress');
          bar3.style.transition = 'none';
          bar3.style.width = '0%';
          void bar3.offsetWidth;
          bar3.style.transition = '';
          bar3.style.width = target3 + '%';
          if (parent3) parent3.classList.remove('filled');
          const onBar3End = function() {
            if (parseFloat(target3) === 100 && parent3) parent3.classList.add('filled');
            bar3.removeEventListener('transitionend', onBar3End);
          };
          bar3.addEventListener('transitionend', onBar3End);
        }
      } else if (bar3) {
        const target3 = bar3.getAttribute('data-progress') || '0';
        const parent3 = bar3.closest('.cart-drawer__goal-progress');
        bar3.style.transition = 'none';
        bar3.style.width = '0%';
        void bar3.offsetWidth;
        bar3.style.transition = '';
        bar3.style.width = target3 + '%';
        if (parent3) parent3.classList.remove('filled');
        const onBar3End = function() {
          if (parseFloat(target3) === 100 && parent3) parent3.classList.add('filled');
          bar3.removeEventListener('transitionend', onBar3End);
        };
        bar3.addEventListener('transitionend', onBar3End);
      }
    }, 150);
  }

  /**
   * Initialize Auto Gift Manager for cart drawer
   */
  initializeAutoGiftManager() {
    console.log('🎁 [Auto Gift] Initializing Auto Gift Manager...');
    console.log('🎁 [Auto Gift] Window object:', typeof window);
    console.log('🎁 [Auto Gift] Window.cartDrawerSettings:', /** @type {any} */ (window).cartDrawerSettings);
    
    // Load gift rules from settings
    const settings = getCartDrawerSettings();
    console.log('🎁 [Auto Gift] Settings from getCartDrawerSettings():', settings);
    
    const threshold = settings.goalProduct1Threshold || 0;
    const threshold2 = settings.goalProduct2Threshold || 0;
    const threshold3 = settings.goalProduct3Threshold || 0;
    const freeProductVariantId1 = settings.freeProductVariantId1;
    const freeProductVariantId2 = settings.freeProductVariantId2;
    const freeProductVariantId3 = settings.freeProductVariantId3;
    const productHasToBeInCart = settings.productHasToBeInCart;
    
    
    console.log('🎁 [Auto Gift] Settings:', {
      threshold: threshold,
      threshold2: threshold2,
      threshold3: threshold3,
      freeProductVariantId1: freeProductVariantId1,
      freeProductVariantId2: freeProductVariantId2,
      freeProductVariantId3: freeProductVariantId3,
      productHasToBeInCart: productHasToBeInCart,
      cartDrawerSettings: settings
    });
    
    // Build rules array for all available thresholds and variant IDs
    /** @type {{ cartValue: GiftRule[] }} */
    const rules = { cartValue: [] };
    
    // Add rule for threshold 1
    if (threshold > 0 && freeProductVariantId1) {
      rules.cartValue.push({
        id: 'cart_drawer_free_gift_1',
        name: 'Free Gift on Cart Threshold 1',
        minimumValue: threshold / 100, // Convert cents to dollars
        giftProductId: freeProductVariantId1,
        quantity: 1,
        enabled: true,
        property: '_goal_product',
        propertyValue: 'true'
      });
    }
    
    // Add rule for threshold 2
    if (threshold2 > 0 && freeProductVariantId2) {
      rules.cartValue.push({
        id: 'cart_drawer_free_gift_2',
        name: 'Free Gift on Cart Threshold 2',
        minimumValue: threshold2 / 100, // Convert cents to dollars
        giftProductId: freeProductVariantId2,
        quantity: 1,
        enabled: true,
        property: '_goal_product',
        propertyValue: 'true'
      });
    }
    
    // Add rule for threshold 3
    if (threshold3 > 0 && freeProductVariantId3) {
      rules.cartValue.push({
        id: 'cart_drawer_free_gift_3',
        name: 'Free Gift on Cart Threshold 3',
        minimumValue: threshold3 / 100, // Convert cents to dollars
        giftProductId: freeProductVariantId3,
        quantity: 1,
        enabled: true,
        property: '_goal_product',
        propertyValue: 'true'
      });
    }
    
    console.log('🎁 [Auto Gift] Rules configured:', rules);
    
    if (rules.cartValue.length === 0) {
      console.log('🎁 [Auto Gift] No rules configured - no valid thresholds or variant IDs found');
    }

    // Create a simplified Auto Gift Manager specifically for cart drawer
    /** @type {{ rules: { cartValue: GiftRule[] }, cartState: CartState | null, isProcessing: boolean, processCartUpdate: function(): Promise<void>, updateCartState: function(): Promise<void>, applyCartValueRules: function(): Promise<void>, removeUnconfiguredGiftProducts: function(): Promise<void>, hasGiftInCart: function(string, string): boolean, hasRequiredProductInCart: function(number|string): boolean, addGiftToCart: function(string, number, GiftRule): Promise<void>, removeGiftFromCart: function(string, string): Promise<void> }} */
    this.autoGiftManager = {
      rules: rules,
      cartState: null,
      isProcessing: false,

      // Check and apply gift rules
      async processCartUpdate() {
        console.log('🎁 [Auto Gift] Processing cart update...');
        
        if (this.isProcessing) {
          console.log('🎁 [Auto Gift] Already processing, skipping...');
          return;
        }
        
        this.isProcessing = true;
        console.log('🎁 [Auto Gift] Starting cart update process...');
        
        try {
          await this.updateCartState();
          await this.applyCartValueRules();
        } catch (error) {
          console.error('🎁 [Auto Gift] Error processing auto gift:', error);
        } finally {
          this.isProcessing = false;
          console.log('🎁 [Auto Gift] Cart update process completed');
        }
      },

      // Update cart state
      async updateCartState() {
        console.log('🎁 [Auto Gift] Updating cart state...');
        try {
          const response = await fetch('/cart.js');
          if (response.ok) {
            /** @type {CartState} */
            this.cartState = await response.json();
            console.log('🎁 [Auto Gift] Cart state updated:', {
              totalPrice: this.cartState?.total_price,
              itemCount: this.cartState?.item_count,
              items: this.cartState?.items?.map(/** @param {CartItem} item */ item => ({
                id: item.product_id,
                variant_id: item.variant_id,
                quantity: item.quantity,
                properties: item.properties
              }))
            });
          } else {
            console.error('🎁 [Auto Gift] Failed to fetch cart:', response.status, response.statusText);
          }
        } catch (error) {
          console.error('🎁 [Auto Gift] Error updating cart state:', error);
        }
      },

      // Apply cart value rules
      async applyCartValueRules() {
        console.log('🎁 [Auto Gift] Applying cart value rules...');
        
        if (!this.cartState || !this.cartState.items) {
          console.log('🎁 [Auto Gift] No cart state or items, skipping rules');
          return;
        }

        const cartTotal = this.cartState.total_price / 100; // Convert from cents
        console.log('🎁 [Auto Gift] Cart total:', cartTotal, 'Rules to check:', this.rules.cartValue.length);
        
        // First, remove any gift products that are no longer configured in settings
        await this.removeUnconfiguredGiftProducts();
        
        for (const rule of this.rules.cartValue) {
          console.log('🎁 [Auto Gift] Checking rule:', {
            id: rule.id,
            name: rule.name,
            minimumValue: rule.minimumValue,
            giftProductId: rule.giftProductId,
            property: rule.property
          });
          
          const hasGift = this.hasGiftInCart(rule.giftProductId, rule.property);
          console.log('🎁 [Auto Gift] Has gift in cart:', hasGift);
          
          // Check if conditional product requirement is met
          const settings = getCartDrawerSettings();
          const productHasToBeInCart = settings.productHasToBeInCart;
          let conditionalMet = true;
          
          if (productHasToBeInCart) {
            conditionalMet = this.hasRequiredProductInCart(productHasToBeInCart);
            if (conditionalMet) {
              console.log(`%c 🎁 [Auto Gift] Conditional product requirement met - product ${productHasToBeInCart} is in cart`, 'color: green; font-weight: bold;');
            } else {
              console.log(`%c 🎁 [Auto Gift] Conditional product requirement NOT met - product ${productHasToBeInCart} is NOT in cart`, 'color: red; font-weight: bold;');
            }
          }
          
          if (cartTotal >= rule.minimumValue && !hasGift && conditionalMet) {
            console.log(`%c 🎁 [Auto Gift] Cart meets threshold (${cartTotal} >= ${rule.minimumValue}) and condition met, adding gift product ${rule.giftProductId}...`, 'color: green; font-weight: bold;');
            await this.addGiftToCart(rule.giftProductId, rule.quantity, rule);
          } else if ((cartTotal < rule.minimumValue || !conditionalMet) && hasGift) {
            console.log(`%c 🎁 [Auto Gift] Cart below threshold (${cartTotal} < ${rule.minimumValue}) or condition not met, removing gift product ${rule.giftProductId}...`, 'color: red; font-weight: bold;');
            await this.removeGiftFromCart(rule.giftProductId, rule.property);
          } else {
            console.log(`%c 🎁 [Auto Gift] No action needed for gift product ${rule.giftProductId} (cart: ${cartTotal}, threshold: ${rule.minimumValue}, hasGift: ${hasGift}, conditionalMet: ${conditionalMet})`, 'color: orange; font-weight: bold;');
          }
        }
      },

      // Remove gift products that are no longer configured in settings
      async removeUnconfiguredGiftProducts() {
        console.log('🎁 [Auto Gift] Checking for unconfigured gift products...');
        
        if (!this.cartState || !this.cartState.items) {
          console.log('🎁 [Auto Gift] No cart state or items, skipping unconfigured gift removal');
          return;
        }
        
        // Get all configured gift product variant IDs
        const configuredVariantIds = this.rules.cartValue.map(rule => parseInt(rule.giftProductId));
        console.log('🎁 [Auto Gift] Configured variant IDs:', configuredVariantIds);
        
        // Find all gift products in cart that are not in the configured list
        const unconfiguredGifts = this.cartState.items.filter(/** @param {CartItem} item */ item => 
          item.properties && 
          /** @type {any} */ (item.properties)._goal_product === 'true' && 
          !configuredVariantIds.includes(item.variant_id)
        );
        
        console.log('🎁 [Auto Gift] Unconfigured gift products found:', unconfiguredGifts.length);
        
        // Remove each unconfigured gift product
        for (const giftItem of unconfiguredGifts) {
          console.log(`%c 🎁 [Auto Gift] Removing unconfigured gift product ${giftItem.variant_id}...`, 'color: red; font-weight: bold;');
          await this.removeGiftFromCart(giftItem.variant_id.toString(), '_goal_product');
        }
      },

      // Check if gift is in cart
      /** @param {string} giftProductId @param {string} property @returns {boolean} */
      hasGiftInCart(giftProductId, property) {
        if (!this.cartState || !this.cartState.items) {
          return false;
        }
        
        const hasGift = this.cartState.items.some(/** @param {CartItem} item */ item => {
          const matches = item.variant_id === parseInt(giftProductId) && 
            item.properties && 
            /** @type {any} */ (item.properties)[property] === 'true';
          
          if (matches) {
            console.log('🎁 [Auto Gift] Found gift item:', {
              product_id: item.product_id,
              variant_id: item.variant_id,
              properties: item.properties
            });
          }
          
          return matches;
        });
        
        console.log('🎁 [Auto Gift] Gift check result:', {
          giftProductId: giftProductId,
          property: property,
          hasGift: hasGift
        });
        
        return hasGift;
      },

      // Check if required product is in cart
      /** @param {number|string} requiredProductId @returns {boolean} */
      hasRequiredProductInCart(requiredProductId) {
        if (!this.cartState || !this.cartState.items) {
          return false;
        }
        
        const requiredId = typeof requiredProductId === 'number' ? requiredProductId : parseInt(requiredProductId);
        
        const hasRequired = this.cartState.items.some(/** @param {CartItem} item */ item => {
          return item.product_id === requiredId;
        });
        
        if (hasRequired) {
          console.log(`%c 🎁 [Auto Gift] Required product IS IN CART: product ${requiredProductId}`, 'color: green; font-weight: bold;');
        } else {
          console.log(`%c 🎁 [Auto Gift] Required product NOT in cart: product ${requiredProductId}`, 'color: red; font-weight: bold;');
        }
        
        return hasRequired;
      },

      // Add gift to cart
      /** @param {string} productId @param {number} quantity @param {GiftRule} rule */
      async addGiftToCart(productId, quantity, rule) {
        console.log('🎁 [Auto Gift] Adding gift to cart:', {
          productId: productId,
          quantity: quantity,
          rule: rule
        });
        
        try {
          const giftData = {
            id: productId,
            quantity: quantity,
            properties: {
              [rule.property]: rule.propertyValue
            }
          };

          console.log('🎁 [Auto Gift] Sending gift data:', giftData);

          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify(giftData)
          });

          console.log('🎁 [Auto Gift] Add gift response:', {
            ok: response.ok,
            status: response.status,
            statusText: response.statusText
          });

          if (response.ok) {
            console.log('🎁 [Auto Gift] ✅ Free gift added to cart successfully');
            // Trigger cart update to refresh the drawer
            document.dispatchEvent(new CartUpdateEvent({}, 'auto-gift-manager'));
          } else {
            const errorText = await response.text();
            console.error('🎁 [Auto Gift] ❌ Failed to add gift:', errorText);
          }
        } catch (error) {
          console.error('🎁 [Auto Gift] ❌ Error adding gift to cart:', error);
        }
      },

      // Remove gift from cart
      /** @param {string} productId @param {string} property */
      async removeGiftFromCart(productId, property) {
        console.log('🎁 [Auto Gift] Removing gift from cart:', {
          productId: productId,
          property: property
        });
        
        try {
          if (!this.cartState || !this.cartState.items) {
            console.log('🎁 [Auto Gift] No cart state or items, cannot remove gift');
            return;
          }
          
          const giftItem = this.cartState.items.find(/** @param {CartItem} item */ item => 
            item.variant_id === parseInt(productId) && 
            item.properties && 
            /** @type {any} */ (item.properties)[property] === 'true'
          );

          console.log('🎁 [Auto Gift] Gift item to remove:', giftItem);

          if (giftItem) {
            const removeData = {
              id: giftItem.key,
              quantity: 0
            };
            
            console.log('🎁 [Auto Gift] Sending remove data:', removeData);

            const response = await fetch('/cart/change.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(removeData)
            });

            console.log('🎁 [Auto Gift] Remove gift response:', {
              ok: response.ok,
              status: response.status,
              statusText: response.statusText
            });

            if (response.ok) {
              console.log('🎁 [Auto Gift] ✅ Free gift removed from cart successfully');
              // Trigger cart update to refresh the drawer
              document.dispatchEvent(new CartUpdateEvent({}, 'auto-gift-manager'));
            } else {
              const errorText = await response.text();
              console.error('🎁 [Auto Gift] ❌ Failed to remove gift:', errorText);
            }
          } else {
            console.log('🎁 [Auto Gift] No gift item found to remove');
          }
        } catch (error) {
          console.error('🎁 [Auto Gift] ❌ Error removing gift from cart:', error);
        }
      }
    };

    // Listen for the theme's cart update event
    document.addEventListener(ThemeEvents.cartUpdate, () => {
      console.log('🎁 [Auto Gift] Theme cart update event received');
      console.log('🎁 [Auto Gift] Auto gift manager exists:', !!this.autoGiftManager);
      if (this.autoGiftManager) {
        console.log('🎁 [Auto Gift] Processing theme cart update...');
        this.autoGiftManager.processCartUpdate();
      } else {
        console.log('🎁 [Auto Gift] Auto gift manager not available');
      }
    });
    
    // Make Auto Gift Manager available globally for testing
    /** @type {any} */ (window).testAutoGiftManager = () => {
      console.log('🎁 [Auto Gift] Manual test triggered');
      if (this.autoGiftManager) {
        this.autoGiftManager.processCartUpdate();
      } else {
        console.log('🎁 [Auto Gift] Auto gift manager not available for testing');
      }
    };
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
